'use client';

import { useEffect, useState } from 'react';
import { PageHero } from '@/components/shared/PageHero';
import { SectionLabel } from '@/components/shared/SectionLabel';
import { ServiceCard } from '@/components/shared/ServiceCard';
import { Link } from '@/components/shared/Link';
import { useTranslations } from '@/i18n/intl';
import { UploadedImage } from '@/components/shared/UploadedImage';
import { RichText } from '@/components/shared/RichText';
import { getServicesPageData } from '@/lib/actions/services';

export default function ServicesPage() {
  const t = useTranslations('Expertise');
  const tCommon = useTranslations('Common');
  const [categories, setCategories] = useState<any[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    void getServicesPageData().then((data) => {
      setCategories(data);
      setReady(true);
    });
  }, []);

  if (!ready) return null;

  return (
    <>
      <PageHero
        title={tCommon('expertise')}
        subtitle={t('introSubtitle')}
        breadcrumbs={[
          { label: tCommon('home'), href: '/' },
          { label: tCommon('expertise') },
        ]}
      />

      <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-white">
        <div className="max-w-[1170px] mx-auto">
          <div className="text-center mb-16">
            <SectionLabel text={t('whatWeDo')} className="text-center" />
            <h2
              className="text-[32px] md:text-[40px] font-bold text-navy leading-snug max-w-[800px] mx-auto font-heading"
            >
              {t('maximizeAdvantage')}
            </h2>
          </div>

          <div className="space-y-24">
            {categories.map((category) => (
              <div key={category.id} className="space-y-10" id={category.slug}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div className={category.order % 2 === 1 ? 'lg:order-2' : ''}>
                    {category.image && (
                      <div className="aspect-video relative rounded-xl overflow-hidden shadow-lg border border-light">
                        <UploadedImage
                          src={category.image}
                          alt={category.name}
                          fill
                          className="object-cover"
                        />
                      </div>
                    )}
                  </div>
                  <div className={category.order % 2 === 1 ? 'lg:order-1' : ''}>
                    <h3 className="text-[28px] md:text-[34px] font-bold text-navy mb-4 font-heading">
                      {category.name}
                    </h3>
                    <RichText
                      html={category.description || ''}
                      className="text-[16px] text-gray leading-relaxed font-sans [&_p]:mb-4"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-[24px]">
                  {category.services.map((service: any) => (
                    <ServiceCard
                      key={service.slug}
                      title={service.title}
                      description={service.shortDescription}
                      href={`/services/${service.slug}`}
                      image={service.homeImage || service.image}
                    />
                  ))}
                </div>
              </div>
            ))}

            {categories.length === 0 && (
              <div className="text-center py-20 bg-light/10 rounded-xl border border-dashed border-light">
                <p className="text-gray italic font-sans">{t('noServices')}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-light/30">
        <div className="max-w-[1170px] mx-auto text-center">
          <SectionLabel text={tCommon('letsDiscuss')} className="text-center" />
          <h2
            className="text-[32px] md:text-[40px] font-bold text-navy leading-snug mb-6 font-heading"
          >
            {t('readyTransform')}
          </h2>
          <p className="text-[16px] text-gray leading-relaxed mb-8 font-sans">
            {t('contactDescription')}
          </p>
          <Link
            href="/contact-us"
            className="inline-flex items-center bg-navy text-white text-[14px] font-semibold px-[25px] py-[11px] rounded-[25px] hover:bg-primary transition-all duration-300"
          >
            {tCommon('contactUs')}
          </Link>
        </div>
      </section>
    </>
  );
}
